import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent } from './ui/card';
import { Mail, Phone, MapPin } from 'lucide-react';
import { serviceOptions } from '../data/mock';

export const ContactSection = () => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    services: [], // Changed from 'service' to 'services' array for multiple selections
    consent: false
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle checkboxes for services (multiple selection)
    if (name === 'services' && type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        services: checked
          ? [...prev.services, value] // Add service to array
          : prev.services.filter(s => s !== value) // Remove service from array
      }));
    } else {
      // Handle other inputs
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setShowError(false);
    setShowSuccess(false);

    try {
      // Submit to Zapier webhook
      const zapierWebhookUrl = process.env.REACT_APP_ZAPIER_WEBHOOK_URL || 'https://hooks.zapier.com/hooks/catch/your-webhook-id/';
      
      const response = await fetch(zapierWebhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        // Only fire Google Ads conversion on successful Zapier response
        if (window.gtag) {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-18232866587/fZGRCMuqtb0cEJvujfZD'
          });
        }
        
        setShowSuccess(true);
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: '',
          services: [],
          consent: false
        });
      } else {
        // Do not fire conversion if Zapier returns error
        setShowError(true);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      // Do not fire conversion if submission fails
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
            Ready to Simplify Your Compliance?
          </h2>
          <p className="text-lg text-muted-foreground">
            Book your free consultation today and discover how we can support your business growth.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div>
            <h3 className="text-2xl font-bold text-primary mb-6">
              Get In Touch
            </h3>
            <p className="text-muted-foreground mb-8">
              Have questions about our services? We're here to help. Reach out to our team and let's discuss how we can support your business.
            </p>

            <div className="space-y-6">
              <Card className="border-2 hover:border-secondary transition-colors">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="bg-secondary/10 p-3 rounded-lg">
                    <Mail className="text-secondary" size={24} />
                  </div>
                  <div>
                    <div className="font-semibold text-primary mb-1">Email</div>
                    <a
                      href="mailto:info@shineaccounting.co"
                      className="text-muted-foreground hover:text-secondary transition-colors"
                    >
                      info@shineaccounting.co
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-secondary transition-colors">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="bg-secondary/10 p-3 rounded-lg">
                    <Phone className="text-secondary" size={24} />
                  </div>
                  <div>
                    <div className="font-semibold text-primary mb-1">Phone / WhatsApp</div>
                    <a
                      href="tel:+971544223506"
                      className="text-muted-foreground hover:text-secondary transition-colors block"
                    >
                      +971 544223506
                    </a>
                    <a
                      href="https://wa.me/971544223506"
                      className="text-secondary hover:text-secondary/80 transition-colors text-sm mt-1 inline-block"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Chat on WhatsApp →
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-secondary transition-colors">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="bg-secondary/10 p-3 rounded-lg">
                    <MapPin className="text-secondary" size={24} />
                  </div>
                  <div>
                    <div className="font-semibold text-primary mb-1">Location</div>
                    <p className="text-muted-foreground">
                      Dubai, United Arab Emirates
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Contact Form - Using Zapier Webhook */}
          <Card className="border-2">
            <CardContent className="p-8">
              {showSuccess && (
                <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                  <p className="text-green-800 font-semibold text-center">
                    Thank you for contacting Shine Core Advisory LLC. Our team will contact you shortly.
                  </p>
                </div>
              )}

              {showError && (
                <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                  <p className="text-red-800 font-semibold text-center">
                    Something went wrong. Please try again or contact us directly.
                  </p>
                </div>
              )}

              <form 
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="company">Company Name *</Label>
                  <Input
                    id="company"
                    name="company"
                    type="text"
                    placeholder="Your Company LLC"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+971 544223506"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label className="mb-3 block">Services Required</Label>
                  <div className="grid grid-cols-1 gap-3">
                    {serviceOptions.map((service) => (
                      <div key={service} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={service.replace(/\s+/g, '-')}
                          name="services"
                          value={service}
                          checked={formData.services.includes(service)}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                          className="w-4 h-4 text-secondary bg-gray-100 border-gray-300 rounded focus:ring-secondary focus:ring-2 cursor-pointer"
                        />
                        <label
                          htmlFor={service.replace(/\s+/g, '-')}
                          className="text-sm font-medium leading-none cursor-pointer select-none"
                        >
                          {service}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="message">Message (Optional)</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Tell us about your business needs..."
                    value={formData.message}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    rows={4}
                    className="mt-2"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold transition-all duration-200"
                >
                  {isSubmitting ? 'Sending...' : 'Book Your Free Consultation'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
